import { api } from '../api/client';
import {
	API_COLLECTION_IMPORTS_URL,
	API_IMPORT_UNDO_PREVIEW_URL,
	API_IMPORT_UNDO_URL,
} from '../utils';

/**
 * A collection's imports (keepr-api docs/SCHEMA.md § Imports): the batch
 * ingest calls an assistant or a script made, grouped by the `importId` its
 * client sent on every batch. Newest first. A row:
 *
 *   { importKey, importId, runIds, by: { userId, name, keyId, keyName },
 *     firstAt, lastAt, created, updated, failed, rows, undoable,
 *     why?: 'undone' | 'no_request_id' | 'nothing_written',
 *     undone?: { at, by, requestId, deleted, restored, kept } }
 *
 * Session-only and manage on the API. A 404 reads as "no imports": a web
 * build ahead of its API gets an empty tab, not an error (knowledge:
 * a new endpoint's 404 is "no data").
 */
export async function listImports( collectionId, { limit, before } = {} ) {
	const params = {};
	if ( limit ) params.limit = limit;
	if ( before ) params.before = before;
	try {
		const { data } = await api.get( API_COLLECTION_IMPORTS_URL( collectionId ), { params } );
		return Array.isArray( data ) ? data : [];
	} catch ( error ) {
		if ( error?.response?.status === 404 ) return [];
		throw error;
	}
}

/**
 * What undoing an import would do. Writes nothing.
 *   { undoable, why?, delete, restore, alreadyUndone?, needsReason, ruleWrites,
 *     kept: [{ itemId, title, reason }], keptCount, leftBehind: [{ kind, count }] }
 */
export async function previewImportUndo( collectionId, importKey ) {
	const { data } = await api.post( API_IMPORT_UNDO_PREVIEW_URL( collectionId, importKey ), {} );
	return data;
}

/**
 * Undo it: deletes the items it created and restores the ones it updated,
 * keeping any changed since. `reason` rides on each delete (a card that asks
 * for one is satisfied by it); the API's default is "Undid an import".
 * Resolves { deleted, restored, alreadyUndone?, kept, keptCount, leftBehind, undone }
 * when keepr finished it within the request, or — for a large import —
 * { started: true, requestId, delete, restore, ... } (HTTP 202): keepr is
 * finishing it, and the list shows the import `undoing` until it is stamped.
 * Rejects with the axios error (409 import_undone / import_not_undoable /
 * undo_in_progress / undo_taken_over / import_too_large).
 */
export async function undoImport( collectionId, importKey, { reason } = {} ) {
	const body = {};
	if ( typeof reason === 'string' && reason.trim() ) body.reason = reason.trim();
	const { data } = await api.post( API_IMPORT_UNDO_URL( collectionId, importKey ), body );
	return data;
}
